import React, {useContext} from 'react';
import {useNavigate, Link} from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Drawer, {drawerClasses} from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import {signOut} from 'firebase/auth';
import {useConfirm} from 'material-ui-confirm';
import {Ranks} from '@embertracking/common';

import {auth} from '-/firebase';
import {UserContext} from '-/contexts/User';
import {stationDisplayName} from '@embertracking/common';
import {SettingsContext} from '-/contexts/Settings';

import MenuContent from './MenuContent';
import UserAvatar from '-/components/UserAvatar';
import FirebaseImage from '-/components/FirebaseImage';

export default function({open, toggleDrawer, allItems, preferences}) {
    const navigate = useNavigate();
    const {currentUser} = useContext(UserContext);
    const {image} = useContext(SettingsContext);
    const {email, fullName, role, isStation} = currentUser || {};
    // A station account's currentUser is the station doc spread flat, with a
    // fullName the api composed. Format from the station fields directly rather
    // than trusting that string: the api and the SPA deploy separately, so this
    // is right for a department whose stations are named "Station 1" even
    // before the api-side fix ships (@embertracking/common).
    const displayName = (isStation ? stationDisplayName(currentUser) : fullName) || email;

    const confirm = useConfirm();

    const onLogOut = async() => {
        const {confirmed} = await confirm({
            title: 'Log out?',
            description: 'You will need to sign in again to access your account.',
            confirmationText: 'Log out',
            cancellationText: 'Stay signed in',
            confirmationButtonProps: {color: 'error', variant: 'contained'}
        });

        if (!confirmed) return;

        await signOut(auth);
        localStorage.removeItem('impersonating');

        navigate('/login');
    };

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={toggleDrawer(false)}
            sx={{
                zIndex: theme => theme.zIndex.drawer + 1,
                [`& .${drawerClasses.paper}`]: {
                    minWidth: '80vw',
                    backgroundImage: 'none',
                    backgroundColor: 'background.paper',
                    // iOS Safari renders the drawer up behind the status bar; give it a
                    // defined top edge there only (iOS-only `-webkit-touch-callout`).
                    '@supports (-webkit-touch-callout: none)': {
                        borderTop: '1px solid',
                        borderTopColor: 'divider'
                    }
                }
            }}
        >
            <Stack
                sx={{
                    maxWidth: '80dvw',
                    height: '100%'
                }}
            >
                <Box
                    component={Link}
                    to="/"
                    onClick={toggleDrawer(false)}
                    sx={{
                        mt: 2,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        cursor: 'pointer'
                    }}
                >
                    {image ? (
                        <FirebaseImage image={image} publicAsset sx={{width: '60%'}} />
                    ) : (
                        <img src="/logo.png" alt="Ember Tracking" style={{width: '60%'}} />
                    )}
                </Box>
                <Divider />
                <Stack sx={{flexGrow: 1}}>
                    <MenuContent toggleDrawer={toggleDrawer(false)} allItems={allItems} preferences={preferences} />
                </Stack>
                <Divider />
                <Stack sx={{gap: 1, px: 2, py: 1.5}}>
                    {/* Same as the desktop rail's footer: the identity itself opens
                        your profile, because the avatar and name are what people
                        aim at. The explicit Profile button below stays — on a phone
                        a labelled target is worth more than the tap it saves. A
                        station login has no member profile, so it stays inert. */}
                    <ButtonBase
                        {...(!isStation && {component: Link, to: '/profile', onClick: toggleDrawer(false)})}
                        disabled={isStation}
                        aria-label={isStation ? undefined : 'Open your profile'}
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            minWidth: 0,
                            width: '100%',
                            textAlign: 'left',
                            p: 0.5,
                            m: -0.5,
                            borderRadius: 1
                        }}
                    >
                        <UserAvatar user={currentUser} sizes="small" sx={{width: 36, height: 36}} />
                        <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0}}>
                            <Typography variant="body2" noWrap sx={{fontWeight: 500, lineHeight: '16px'}}>
                                {displayName}
                            </Typography>
                            {!isStation && (
                                <Typography variant="caption" noWrap sx={{color: 'text.secondary'}}>
                                    {Ranks[role]}
                                </Typography>
                            )}
                        </Box>
                    </ButtonBase>
                    <Stack direction="row" sx={{gap: 1, alignItems: 'center'}}>
                        {!isStation && (
                            <Button
                                component={Link}
                                to="/profile"
                                onClick={toggleDrawer(false)}
                                variant="contained"
                                color="secondary"
                                startIcon={<AccountCircleRoundedIcon />}
                                sx={{flex: 1}}
                            >
                                Profile
                            </Button>
                        )}
                        <Tooltip title="Log out">
                            <IconButton onClick={onLogOut} aria-label="Log out">
                                <LogoutRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Stack>
        </Drawer>
    );
}
